import { boundsOfPoints, compose, invert, liquifyBounds, meshBounds, multiply, scaleStoredLiquify, scaleStoredMesh, transformCmds, transformRect, translate, unionRects, type Affine, type Rect, estimatePuppetBounds } from "@canvas/geometry";
import { createNode, newId, scaleFontSizes, type Document, type Mask, type Matrix, type Node, type Warp, scaleEffect } from "@canvas/schema";
import { subtree, type Op } from "./ops";

/** Pure "commands": compute ops for common editor actions. Apply them via a Transaction. */

export function pageOf(doc: Document, id: string): string | null {
  let cur: string | undefined = id;
  for (let i = 0; i < 128 && cur; i++) {
    if (doc.pages.some((p) => p.id === cur)) return cur;
    cur = doc.nodes[cur]?.parentId;
  }
  return null;
}

export function childrenOf(doc: Document, containerId: string): string[] {
  const page = doc.pages.find((p) => p.id === containerId);
  if (page) return page.children;
  const n = doc.nodes[containerId];
  return n?.type === "group" || n?.type === "frame" ? n.children : [];
}

/** Matrix from node-local space to page space. */
export function worldMatrix(doc: Document, id: string): Affine {
  const chain: Matrix[] = [];
  let cur: Node | undefined = doc.nodes[id];
  while (cur) {
    chain.push(cur.transform);
    cur = doc.nodes[cur.parentId];
  }
  // chain = [self, parent, grandparent…]: apply self first.
  return compose(...chain);
}

/** Matrix from page space into the coordinate space of `containerId`'s children. */
export function containerMatrix(doc: Document, containerId: string): Affine {
  return doc.nodes[containerId] ? worldMatrix(doc, containerId) : [1, 0, 0, 1, 0, 0];
}

/** Local-space box of a leaf node, after any warp and liquify; groups have none of their own. */
export function localBox(n: Node): Rect | null {
  const base = baseBox(n);
  const warped = base && n.warp ? warpedBox(n.warp, base, n) : base;
  return warped && n.liquify && n.type !== "group" ? liquifyBounds(n.liquify, warped, liquifyVisibleHook?.(n)) : warped;
}

/**
 * Which local points of a node's content are shown at all (its painted mask, which only the
 * engine has pixels for), so liquify bounds don't grow for hidden content. Registered by the
 * engine; `key` changes whenever the answer can.
 */
let liquifyVisibleHook: ((n: Node) => { key: string; at: (x: number, y: number) => boolean } | null) | null = null;
export function setLiquifyVisibleHook(fn: typeof liquifyVisibleHook) {
  liquifyVisibleHook = fn;
}

/**
 * Exact bounds of a puppet-warped node need its triangulated silhouette (a photo's pixels, a
 * text's glyphs), which only the engine has; it registers this hook. Without it (or before
 * the mesh exists) bounds are estimated from the pins.
 */
let puppetBoundsHook: ((n: Node) => Rect | null) | null = null;
export function setPuppetBoundsHook(fn: ((n: Node) => Rect | null) | null) {
  puppetBoundsHook = fn;
}

/** Bounds of `base` (a node's unwarped box) after `warp` (`n`: the node, for puppet warps). */
export function warpedBox(warp: Warp, base: Rect, n?: Node): Rect {
  switch (warp.type) {
    case "perspective":
      return boundsOfPoints(warp.corners.map(([x, y]) => ({ x, y })));
    case "mesh":
      return meshBounds(base, warp);
    case "puppet":
      return (n && puppetBoundsHook?.(n)) ?? estimatePuppetBounds(base, warp.pins);
  }
}

/** A warp after the node it belongs to is scaled by (kx, ky) about its local origin. */
export function scaleWarp(warp: Warp, kx: number, ky = kx): Warp {
  switch (warp.type) {
    case "perspective":
      return { ...warp, corners: warp.corners.map(([x, y]) => [x * kx, y * ky]) as typeof warp.corners };
    case "mesh":
      return scaleStoredMesh(warp, kx, ky);
    case "puppet":
      return {
        ...warp,
        expansion: warp.expansion * Math.sqrt(Math.abs(kx * ky)),
        pins: warp.pins.map((p) => ({ ...p, rest: [p.rest[0] * kx, p.rest[1] * ky], pos: [p.pos[0] * kx, p.pos[1] * ky] })),
      };
  }
}

/** The node's own (unwarped) box. */
export function baseBox(n: Node): Rect | null {
  switch (n.type) {
    case "group":
      return null;
    case "line":
      return { x: Math.min(0, n.width), y: Math.min(0, n.height), w: Math.abs(n.width), h: Math.abs(n.height) };
    default:
      return { x: 0, y: 0, w: n.width, h: n.height };
  }
}

/** Axis-aligned page-space bounds (geometry only, ignoring stroke width). */
export function worldBounds(doc: Document, id: string): Rect | null {
  const n = doc.nodes[id];
  if (!n) return null;
  if (n.type === "group") {
    if (n.warp?.type === "perspective") return transformRect(worldMatrix(doc, id), warpedBox(n.warp, { x: 0, y: 0, w: 0, h: 0 }));
    return unionRects(n.children.map((c) => worldBounds(doc, c)).filter((r): r is Rect => !!r));
  }
  return transformRect(worldMatrix(doc, id), localBox(n)!);
}

export function selectionBounds(doc: Document, ids: string[]): Rect | null {
  return unionRects(ids.map((id) => worldBounds(doc, id)).filter((r): r is Rect => !!r));
}

/** Drop ids whose ancestor is also in the list (acting on the ancestor covers them). */
export function topLevel(doc: Document, ids: string[]): string[] {
  const set = new Set(ids);
  return ids.filter((id) => {
    let p = doc.nodes[id]?.parentId;
    while (p && doc.nodes[p]) {
      if (set.has(p)) return false;
      p = doc.nodes[p].parentId;
    }
    return !!doc.nodes[id];
  });
}

/** Sort ids by paint order (back to front) across the whole page. */
export function paintOrder(doc: Document, ids: string[]): string[] {
  const order = new Map<string, number>();
  let k = 0;
  const walk = (children: string[]) => {
    for (const c of children) {
      order.set(c, k++);
      const n = doc.nodes[c];
      if (n?.type === "group" || n?.type === "frame") walk(n.children);
    }
  };
  for (const p of doc.pages) walk(p.children);
  return ids.slice().sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0));
}

export function deleteNodes(doc: Document, ids: string[]): Op[] {
  return topLevel(doc, ids).map((id) => ({ op: "remove", id }));
}

/** A linked mask after its node's content is scaled by (kx, ky) about its local origin. */
export function scaleMask(mask: Mask, kx: number, ky = kx): Mask {
  return mask.linked ? { ...mask, transform: multiply([kx, 0, 0, ky, 0, 0], mask.transform) as Matrix } : mask;
}

/** Point a clone's references (mask source nodes) at the clones of what they referenced. */
export function remapRefs(c: Node, map: Map<string, string>) {
  const v = c.mask?.vector;
  if (v?.type === "node" && map.has(v.nodeId)) c.mask = { ...c.mask!, vector: { ...v, nodeId: map.get(v.nodeId)! } };
}

/** Deep-clone subtrees with fresh ids, inserted just above each original, offset by (dx, dy). */
export function duplicateNodes(doc: Document, ids: string[], dx = 20, dy = 20): { ops: Op[]; newIds: string[] } {
  const ops: Op[] = [];
  const newIds: string[] = [];
  for (const id of paintOrder(doc, topLevel(doc, ids))) {
    const nodes = subtree(doc, id);
    const map = new Map(nodes.map((n) => [n.id, newId()]));
    const cloned = nodes.map((n) => {
      const c = structuredClone(n) as Node;
      c.id = map.get(n.id)!;
      if (map.has(c.parentId)) c.parentId = map.get(c.parentId)!;
      if (c.type === "group" || c.type === "frame") c.children = c.children.map((x) => map.get(x)!);
      remapRefs(c, map);
      return c;
    });
    const root = cloned[0];
    root.name = nodes[0].name;
    root.transform = multiply(translate(dx, dy), root.transform) as Matrix;
    const parent = doc.nodes[id].parentId;
    ops.push({ op: "insert", nodes: cloned, parentId: parent, index: childrenOf(doc, parent).indexOf(id) + 1 });
    newIds.push(root.id);
  }
  return { ops, newIds };
}

/**
 * Group nodes under a new group placed where the top-most member was. Members from other
 * containers keep their page position (their transforms are re-expressed in the group).
 */
export function groupNodes(doc: Document, ids: string[]): { ops: Op[]; groupId: string } | null {
  const members = paintOrder(doc, topLevel(doc, ids));
  if (members.length < 2) return null;
  const top = members[members.length - 1];
  const parentId = doc.nodes[top].parentId;
  const group = createNode("group", parentId);
  const ops: Op[] = [{ op: "insert", nodes: [group], parentId, index: childrenOf(doc, parentId).indexOf(top) + 1 }];
  const groupWorld = containerMatrix(doc, parentId); // group has identity transform
  const inv = invert(groupWorld)!;
  members.forEach((id, i) => {
    const n = doc.nodes[id];
    if (n.parentId !== parentId) {
      const world = multiply(containerMatrix(doc, n.parentId), n.transform);
      ops.push({ op: "update", id, set: { transform: multiply(inv, world) as Matrix } });
    }
    ops.push({ op: "move", id, parentId: group.id, index: i });
  });
  return { ops, groupId: group.id };
}

/** Dissolve groups, moving children to the group's parent with transforms baked in. */
export function ungroupNodes(doc: Document, ids: string[]): { ops: Op[]; childIds: string[] } {
  const ops: Op[] = [];
  const childIds: string[] = [];
  for (const id of ids) {
    const g = doc.nodes[id];
    if (!g || g.type !== "group") continue;
    const parentChildren = childrenOf(doc, g.parentId);
    let index = parentChildren.indexOf(id);
    for (const c of g.children) {
      const child = doc.nodes[c];
      ops.push({ op: "update", id: c, set: { transform: multiply(g.transform, child.transform) as Matrix } });
      ops.push({ op: "move", id: c, parentId: g.parentId, index: index++ });
      childIds.push(c);
    }
    ops.push({ op: "remove", id });
  }
  return { ops, childIds };
}

export type ZOrder = "forward" | "backward" | "front" | "back";

export function reorder(doc: Document, ids: string[], how: ZOrder): Op[] {
  const ops: Op[] = [];
  const byParent = new Map<string, string[]>();
  for (const id of topLevel(doc, ids)) {
    const p = doc.nodes[id].parentId;
    byParent.set(p, [...(byParent.get(p) ?? []), id]);
  }
  for (const [parent, members] of byParent) {
    const order = childrenOf(doc, parent).slice();
    const set = new Set(members);
    let next: string[];
    if (how === "front") next = [...order.filter((x) => !set.has(x)), ...order.filter((x) => set.has(x))];
    else if (how === "back") next = [...order.filter((x) => set.has(x)), ...order.filter((x) => !set.has(x))];
    else {
      next = order.slice();
      const step = how === "forward" ? 1 : -1;
      const idxs = next.map((x, i) => (set.has(x) ? i : -1)).filter((i) => i >= 0);
      if (step > 0) idxs.reverse();
      for (const i of idxs) {
        const j = i + step;
        if (j < 0 || j >= next.length || set.has(next[j])) continue;
        [next[i], next[j]] = [next[j], next[i]];
      }
    }
    next.forEach((id, index) => {
      if (set.has(id) && order.indexOf(id) !== index) ops.push({ op: "move", id, parentId: parent, index });
    });
  }
  return ops;
}

export type Align = "left" | "hcenter" | "right" | "top" | "vcenter" | "bottom";

/** Align to `target` (page rect, or the selection bounds when several nodes are selected). */
export function alignNodes(doc: Document, ids: string[], how: Align, target: Rect): Op[] {
  const ops: Op[] = [];
  for (const id of topLevel(doc, ids)) {
    const b = worldBounds(doc, id);
    const n = doc.nodes[id];
    if (!b) continue;
    let dx = 0, dy = 0;
    if (how === "left") dx = target.x - b.x;
    if (how === "hcenter") dx = target.x + target.w / 2 - (b.x + b.w / 2);
    if (how === "right") dx = target.x + target.w - (b.x + b.w);
    if (how === "top") dy = target.y - b.y;
    if (how === "vcenter") dy = target.y + target.h / 2 - (b.y + b.h / 2);
    if (how === "bottom") dy = target.y + target.h - (b.y + b.h);
    if (!dx && !dy) continue;
    ops.push({ op: "update", id, set: { transform: translateInParent(doc, n, dx, dy) } });
  }
  return ops;
}

/** Move a node by a page-space delta, accounting for its parent's transform. */
export function translateInParent(doc: Document, n: Node, dx: number, dy: number): Matrix {
  const inv = invert(containerMatrix(doc, n.parentId));
  const d = inv ? { x: inv[0] * dx + inv[2] * dy, y: inv[1] * dx + inv[3] * dy } : { x: dx, y: dy };
  const t = n.transform;
  return [t[0], t[1], t[2], t[3], t[4] + d.x, t[5] + d.y];
}

// ---------------------------------------------------------------- pages

/** Insert a blank page (same size as `like`) after `afterIndex`. */
export function addPage(doc: Document, afterIndex: number, like = doc.pages[Math.max(0, afterIndex)]): { ops: Op[]; pageId: string } {
  const page = { id: newId(), name: `Page ${doc.pages.length + 1}`, width: like.width, height: like.height, background: like.background, children: [] as string[], guides: like.guides };
  return { ops: [{ op: "page.insert", page, nodes: [], index: afterIndex + 1 }], pageId: page.id };
}

/** Deep-copy a page and all of its content with fresh ids, placed right after it. */
export function duplicatePage(doc: Document, pageId: string): { ops: Op[]; pageId: string } | null {
  const index = doc.pages.findIndex((p) => p.id === pageId);
  if (index < 0) return null;
  const src = doc.pages[index];
  const nodes = src.children.flatMap((id) => subtree(doc, id));
  const map = new Map(nodes.map((n) => [n.id, newId()]));
  const newPageId = newId();
  const cloned = nodes.map((n) => {
    const c = structuredClone(n) as Node;
    c.id = map.get(n.id)!;
    c.parentId = map.get(n.parentId) ?? newPageId;
    if (c.type === "group" || c.type === "frame") c.children = c.children.map((x) => map.get(x)!);
    remapRefs(c, map);
    return c;
  });
  const page = { ...structuredClone(src), id: newPageId, name: `${src.name} copy`, children: src.children.map((c) => map.get(c)!) };
  return { ops: [{ op: "page.insert", page, nodes: cloned, index: index + 1 }], pageId: newPageId };
}

/** Uniformly scale one node's own geometry by `k` (sizes, strokes, radii, effects, type). */
export function scaledNodeProps(n: Node, k: number): Record<string, unknown> {
  const set: Record<string, unknown> = {};
  const r = n as Record<string, unknown>;
  if (typeof r.width === "number") set.width = Math.max(n.type === "line" ? 0 : 0.01, (r.width as number) * k);
  if (typeof r.height === "number") set.height = n.type === "line" ? (r.height as number) * k : Math.max(0.01, (r.height as number) * k);
  if (typeof r.cornerRadius === "number") set.cornerRadius = (r.cornerRadius as number) * k;
  if (Array.isArray(r.strokes)) set.strokes = (r.strokes as { width: number; dash?: number[] }[]).map((s) => ({ ...s, width: s.width * k }));
  if (n.type === "path") set.cmds = transformCmds(n.cmds, [k, 0, 0, k, 0, 0]);
  if (n.type === "text") set.spans = scaleFontSizes(n.spans, k);
  if (n.warp) set.warp = scaleWarp(n.warp, k);
  if (n.liquify) set.liquify = scaleStoredLiquify(n.liquify, k);
  if (n.effects.length)
    set.effects = n.effects.map((e) => scaleEffect(e, k));
  if (n.mask) set.mask = scaleMask(n.mask, k);
  return set;
}

/**
 * Resize a page and scale its content to fit (Canva's "Resize & scale content"): everything is
 * scaled uniformly by min(w/W, h/H) and centred; sizes are baked into the nodes rather than
 * left in transforms so the result edits like hand-made content.
 */
export function resizePageScaled(doc: Document, pageId: string, w: number, h: number): Op[] {
  const page = doc.pages.find((p) => p.id === pageId);
  if (!page) return [];
  const k = Math.min(w / page.width, h / page.height);
  const ox = (w - page.width * k) / 2, oy = (h - page.height * k) / 2;
  const ops: Op[] = [{ op: "page.update", id: pageId, set: { width: w, height: h, guides: (page.guides ?? []).map((g) => ({ ...g, pos: g.pos * k + (g.axis === "x" ? ox : oy) })) } }];
  const walk = (id: string, top: boolean) => {
    const n = doc.nodes[id];
    if (!n) return;
    const t = n.transform;
    const set = scaledNodeProps(n, k);
    set.transform = [t[0], t[1], t[2], t[3], t[4] * k + (top ? ox : 0), t[5] * k + (top ? oy : 0)];
    // Unlinked masks live in the parent's space, which is scaled too.
    if (n.mask && !n.mask.linked) set.mask = { ...n.mask, transform: multiply([k, 0, 0, k, top ? ox : 0, top ? oy : 0], n.mask.transform) as Matrix };
    ops.push({ op: "update", id, set: set as Partial<Node> });
    if (n.type === "group" || n.type === "frame") n.children.forEach((c) => walk(c, false));
  };
  page.children.forEach((c) => walk(c, true));
  return ops;
}

/**
 * Canvas size (Photoshop): change the page's size without scaling anything. The content keeps
 * its place relative to `anchor` — fractions (0 = left/top, 0.5 = centre, 1 = right/bottom).
 */
export function resizeCanvasOps(doc: Document, pageId: string, w: number, h: number, anchor: [number, number]): Op[] {
  const page = doc.pages.find((p) => p.id === pageId);
  if (!page || (w === page.width && h === page.height)) return [];
  const dx = (w - page.width) * anchor[0], dy = (h - page.height) * anchor[1];
  const ops: Op[] = [{ op: "page.update", id: pageId, set: { width: w, height: h, guides: page.guides.map((g) => ({ ...g, pos: g.pos + (g.axis === "x" ? dx : dy) })) } }];
  if (dx || dy) {
    for (const id of page.children) {
      const t = doc.nodes[id]?.transform;
      if (t) ops.push({ op: "update", id, set: { transform: [t[0], t[1], t[2], t[3], t[4] + dx, t[5] + dy] } });
    }
  }
  return ops;
}

// ---------------------------------------------------------------- recolour

type Json = unknown;
const isColor = (v: Json): v is string => typeof v === "string" && /^#[0-9a-f]{8}$/i.test(v);
/** Keys whose colour values are part of a node's look (ids, names etc. are never colours). */
const COLOR_KEYS = new Set(["fills", "strokes", "spans", "effects", "effect", "duotone", "paint", "fill", "stops", "style", "color", "shadow", "highlight", "palette"]);

function mapColors(v: Json, f: (c: string) => string, key = ""): Json {
  if (isColor(v)) return key ? f(v) : v;
  if (Array.isArray(v)) return v.map((x) => mapColors(x, f, key));
  if (v && typeof v === "object") {
    const out: Record<string, Json> = {};
    for (const [k, x] of Object.entries(v)) out[k] = COLOR_KEYS.has(k) || key ? mapColors(x, f, k) : x;
    return out;
  }
  return v;
}

const RECOLOR_FIELDS = ["fills", "strokes", "spans", "effects", "effect", "duotone"] as const;

/** Every node in the subtrees of `ids`. */
function deep(doc: Document, ids: string[]): Node[] {
  return ids.flatMap((id) => subtree(doc, id));
}

/**
 * Distinct colours used by the given nodes (fills, borders, gradients, text, effects), most
 * used first, compared without alpha so "#ff0000" at any opacity is one swatch.
 */
export function collectColors(doc: Document, ids: string[]): string[] {
  const count = new Map<string, number>();
  for (const n of deep(doc, ids))
    for (const k of RECOLOR_FIELDS)
      if (k in n) mapColors((n as Record<string, Json>)[k], (c) => (count.set(c.slice(0, 7).toLowerCase(), (count.get(c.slice(0, 7).toLowerCase()) ?? 0) + 1), c), k);
  return [...count.entries()].sort((a, b) => b[1] - a[1]).map(([c]) => c);
}

/** Replace colour `from` (#rrggbb, any alpha) with `to` (#rrggbb, alpha kept) across the nodes. */
export function recolorOps(doc: Document, ids: string[], from: string, to: string): Op[] {
  const f = from.slice(0, 7).toLowerCase(), t = to.slice(0, 7).toLowerCase();
  const ops: Op[] = [];
  for (const n of deep(doc, ids)) {
    const set: Record<string, Json> = {};
    for (const k of RECOLOR_FIELDS) {
      if (!(k in n)) continue;
      const before = (n as Record<string, Json>)[k];
      const after = mapColors(before, (c) => (c.slice(0, 7).toLowerCase() === f ? t + c.slice(7) : c), k);
      if (JSON.stringify(after) !== JSON.stringify(before)) set[k] = after;
    }
    if (Object.keys(set).length) ops.push({ op: "update", id: n.id, set: set as Partial<Node> });
  }
  return ops;
}
